<script setup lang="ts">
/**
 * ConfirmDialog — editorial-styled modal that replaces native
 * window.confirm() platform-wide.
 *
 * Mount once per portal (typically in MainLayout.vue). Any view can
 * trigger it via useConfirm().confirm({...}). The styles assume the
 * standard .modal / .modal-backdrop / .btn / .modal-icon classes
 * already in product.css.
 */
import { onMounted, onUnmounted } from 'vue';
import { useConfirm } from '../composables/useConfirm';

const { state, answer } = useConfirm();

// Escape key closes as cancel. We register/unregister on lifecycle so
// the listener doesn't leak across portal navigation.
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && state.value.open) answer(false);
}
onMounted(() => window.addEventListener('keydown', onKey));
onUnmounted(() => window.removeEventListener('keydown', onKey));

function resolveIcon(): string {
  if (state.value.icon) return state.value.icon;
  return state.value.tone === 'danger' ? 'warning' : 'help';
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="state.open"
      class="modal-backdrop is-open"
      @click.self="answer(false)"
    >
      <div class="modal" @click.stop>
        <div class="modal__head">
          <div class="modal-head-row">
            <div
              class="modal-icon"
              :class="{ 'modal-icon--danger': state.tone === 'danger' }"
            >
              <span class="material-icons">{{ resolveIcon() }}</span>
            </div>
            <div>
              <h3 class="modal__title">{{ state.title }}</h3>
              <p v-if="state.subtitle" class="modal__subtitle">{{ state.subtitle }}</p>
            </div>
          </div>
        </div>
        <div class="modal__body">
          <p class="modal-body-text">{{ state.message }}</p>
        </div>
        <div class="modal__foot">
          <div class="modal__foot-buttons">
            <button @click="answer(false)" class="btn btn--ghost">
              {{ state.cancelLabel }}
            </button>
            <button
              @click="answer(true)"
              class="btn"
              :class="state.tone === 'danger' ? 'btn--danger' : 'btn--primary'"
              autofocus
            >
              {{ state.confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
